import { attachBridge } from "../bridge/relayClient";
import { BridgeDispatcher } from "../bridge/dispatcher";
import { declare } from "../bridge/declare";
import { hostToolHandlers, sdkToolHandlers } from "../bridge/hostTools";
import { readProjectIdentity } from "../bridge/projectIdentity";
import { realHostAdapter } from "../host/realAdapter";
import { FRAME_ANSWERED_TOOLS } from "../manifest/tools";
import { isJsonObject } from "../wire/json";
import type { TokenListing } from "../wire/protocol";
import { type FrontInputs, sessionLine, shouldSendFront } from "./controlFront";
import { followProject } from "./controlProject";
import { followHostTheme } from "./controlTheme";
import panelCss from "./controlPanel.css";
import mondrianCss from "./mondrian-tokens.css";
import { problemLine } from "./panelDrop";
import { nameFor, UNLISTED_NOTE } from "./panelNames";

/**
 * The MCP control app: identity, a **Connect** button, and a session picture.
 *
 * **It is what makes a project the agent's target** (#67). When its tab comes
 * to the front it sends `front`, and the relay makes this project active — but
 * only because this panel's token carries the control app's `app_id`, which
 * Giraffe mints only where Django has enabled the app. Nothing in this file
 * asserts that; the relay reads it off the token.
 *
 * This is #16's FIRST PASS, not #16. There is no per-app enable/disable here —
 * that needs the `Origin` allowlist, which is #22 — and the app declares no
 * tools of its own. Resist growing it.
 *
 * ## Where it runs, and why that is a faithful proxy and not a shortcut
 *
 * It is served from `https://mcp-control.giraffe.build` by `workers/control/`
 * and must be placed as the **registered Giraffe MCP app** (app id 277, the
 * deploy's `CONTROL_APP_ID`) on the v6 frontend. The generic per-project iframe
 * panel (`appName: "Iframe"`, app id 167) still loads it and still connects,
 * but its token says 167, so the relay ignores its `front` and the panel says
 * so.
 *
 * `scripts/dev-server.mjs` still serves the identical bundle on
 * `http://localhost:5174`, and the session picture works there. **Connect does
 * not**: localhost is a different site from the relay, so the relay refuses
 * the origin and the cookie could not be first-party anyway.
 *
 * ## What `localhost` actually buys, read from the host source
 *
 * **Not the token.** `getUserClaimsJwt`'s only gate is `requireCallerAppKey`
 * (`libs/project-core/src/iframeAppRegistry.ts:16`), a `WeakMap<Window,
 * appKey>` written unconditionally by `Parent`'s `onLoad`
 * (`libs/builtin-apps/src/lib/Parent/index.tsx:42`) with **no origin check**.
 *
 * `isGiraffeBuildHost` (localhost / 127.0.0.1 / [::1] / https://*.giraffe.build)
 * gates other things, none of which this app calls: `getProjectBundle`'s
 * consent-free path, `bulkCreateProjects`, `fetchWorkspaceDetails`,
 * `updateWorkspaceDetails`, `navigateTo` via `isPrivilegedUrl`, and the
 * confirmation `createApp`, `updateApp` and `deleteApp` show (re-read at
 * `50cad4903`, 2026-10-12).
 *
 * ⚠️ Provenance: a HOST-SOURCE READING at `edf19672a`, 2026-09-14 — verified
 * against the current build, **not a contract**, and **never run from a
 * non-localhost origin**. That last part was issue #21, answered YES on
 * 2026-09-14 by the deployed control app itself.
 */

const scriptParams = new URL(import.meta.url).searchParams;

/**
 * ⚠️ Read from the HOST, not from this frame's URL — which inside an app panel
 * is the APP's address and says nothing about the project.
 *
 * `readProjectIdentity` is SHARED by every entry point deliberately. Every
 * frame in one session must derive its workspace id the same way, or two
 * frames in the same Giraffe workspace register under different ids and never
 * appear in one session — a failure that presents as "the second frame never
 * joined" and sends the reader to the socket.
 */
const host = realHostAdapter();
const frameId = `control-${Math.random().toString(36).slice(2, 8)}`;

const relayUrl = scriptParams.get("relay") ?? "";
const relayOrigin = scriptParams.get("relayHttp") ?? "";

/**
 * Palette, then styling, then the host's theme, then the markup.
 *
 * ⚠️ Both stylesheets ride in THIS BUNDLE and are injected as one `<style>`.
 * They are not `<link>`ed and they are not written into either page template.
 * The reason is the CSP (`default-src 'none'`, `style-src 'unsafe-inline'`),
 * which covers a script-inserted `<style>` and would not cover a stylesheet
 * request without adding `'self'`; see `controlPanel.css`. The second reason is
 * that the page exists twice — `controlWorker.ts` writes the one that ships and
 * `scripts/dev-server.mjs` writes the one for `localhost:5174` — and neither
 * copy can drift in how the panel looks if neither copy describes it.
 *
 * Tokens first so the file a reader opens second is the one that uses them.
 */
const style = document.createElement("style");
style.textContent = `${mondrianCss}\n${panelCss}`;
document.head.appendChild(style);

/**
 * The panel wears GIRAFFE's theme, read from `prefers-color-scheme` — which
 * inside an app frame reports the host's lightness rather than the OS's. See
 * `controlTheme.ts` for why that works and what it cannot tell us.
 */
followHostTheme(document.documentElement, window.matchMedia("(prefers-color-scheme: dark)"));

const panel = document.createElement("div");
panel.innerHTML = `
  <header class="gm-head">
    <h1>Giraffe MCP</h1>
    <span class="gm-badge" id="session">…</span>
  </header>
  <dl class="gm-rows">
    <dt>Signed in as</dt>
    <dd class="gm-named"><span class="gm-name" id="who">…</span><code id="who-id"></code></dd>
    <dt>Socket</dt>
    <dd id="socket-row"><span class="gm-dot"></span><span id="socket">connecting</span></dd>
    <dt>Project</dt>
    <dd class="gm-named"><span class="gm-name" id="project-name"></span><code id="project"></code></dd>
    <dt>Workspace</dt>
    <dd class="gm-named"><span class="gm-name" id="workspace-name"></span><code id="workspace"></code></dd>
  </dl>
  <div class="gm-actions">
    <button id="connect" class="gm-btn gm-btn--primary" type="button">Connect</button>
    <button id="retry" class="gm-btn" type="button" hidden>Retry</button>
  </div>
  <p class="gm-status" id="target" role="status"></p>
  <p class="gm-status gm-problem" id="problem" role="status"></p>
  <p class="gm-status" id="status" role="status"></p>
`;
document.body.appendChild(panel);

/**
 * **The control frame is self-sufficient**, and that is the design rather than
 * a convenience: one panel open and every tool in the main list answers, with
 * no second app required for anything.
 *
 * ## What this replaced
 *
 * ⚠️ This used to read *"No tools. The control app is a surface for the
 * SESSION, not a tool provider"* — and it conflated the two sets
 * `bridge/dispatcher.ts` separates in as many words: **ADVERTISED and
 * IMPLEMENTED are different sets.** "Provides no app tools" was right;
 * "implements nothing" was the accident. The control app registers as an
 * ordinary frame, so a frame that answered nothing became the newest frame in
 * the workspace the moment a user opened this panel to press Connect — and
 * every fixed host tool then failed with a capable frame connected and never
 * asked. See `relay/sessions.ts`'s `resolveImplementor`.
 *
 * It still advertises **no `control__*` app tools**, so `giraffe_app_tools` is
 * unchanged by its presence.
 *
 * `FRAME_ANSWERED_TOOLS` is the fixed host set PLUS the generic SDK tools. This
 * frame is what GUARANTEES them; another app may declare them too, at its
 * author's choice (#25).
 */
const dispatcher = new BridgeDispatcher(declare(FRAME_ANSWERED_TOOLS), {
  ...hostToolHandlers(host),
  ...sdkToolHandlers(host),
});

/**
 * Identity FIRST, then the bridge.
 *
 * Nothing is attached until the host has said where this frame is. A bridge
 * started with a placeholder workspace id would register successfully, show a
 * connected panel, and sit in a session of its own — which is worse than not
 * starting, because it looks like it worked.
 */
const located = await readProjectIdentity(host);
if (!located.ok) {
  setStatus(located.reason);
  throw new Error(located.reason);
}
// Mutable: `followProject` below moves it when the tab switches project in place.
let place = located.identity;
text("project", place.projectId);
text("workspace", place.workspaceId);

let connected = false;
// Survives a dropped socket on purpose: it is what lets a reconnect re-claim
// the project the relay forgot. See `shouldSendFront`.
let claimed = false;
let last: FrontInputs = { visible: false, connected: false, claimed: false };
function claimIfFront(): void {
  const next = { visible: document.visibilityState === "visible", connected, claimed };
  if (shouldSendFront(last, next)) bridge.front();
  last = next;
}

const bridge = attachBridge({
  relayUrl,
  frameId,
  app: "control",
  ...place,
  dispatcher,
  getToken: () => host.userToken(),
  reportUrl: relayOrigin === "" ? undefined : new URL("/report", relayOrigin).toString(),
  onStatus: (status) => {
    const socket = status.connected ? "connected" : "disconnected";
    // The row names the KIND of drop, so the word someone reads back to us
    // already says which failure it was; the status line says what to do.
    text("socket", status.drop?.kind ?? socket);
    // The same fact twice, for two kinds of reader: the word for anyone reading
    // the panel, and the dot for anyone glancing at it. The dot is CSS's to
    // colour — see `[data-socket]` in controlPanel.css.
    attr("socket-row", "data-socket", socket);
    // Who the RELAY says we are, which is a verified fact and not a credential.
    // ⚠️ Null is "not told yet", never "nobody" — rendering the two the same
    // way is #10 in the other direction.
    const identity = status.identity;
    text("who", identity === null ? "…" : (identity.userName ?? ""));
    text("who-id", identity?.userId ?? "");
    // The frame's REPORTED ids, matched against the relay's VERIFIED lists.
    // The ids stay printed whatever the match says.
    showName("project-name", place.projectId, identity?.projects ?? null);
    showName("workspace-name", place.workspaceId, identity?.workspaces ?? null);
    text(
      "session",
      status.session === null
        ? "…"
        : `${status.session.state}${status.session.activeHere ? " (here)" : ""}`,
    );
    // ⚠️ The RAW state name, not a display name. `live` is the one state the
    // badge colours; the other two are the session answering correctly and
    // stay neutral rather than reading as faults.
    attr("session", "data-state", status.session?.state ?? "");
    text("target", sessionLine(status.session));
    connected = status.connected;
    if (status.session !== null) claimed = status.session.activeHere;
    claimIfFront();
    // ⚠️ The raw message goes to the CONSOLE, never to the panel. It is
    // whatever the host SDK or the network threw, so unlike our own strings its
    // content is not ours to bound — no amount of editing this repo's messages
    // makes it safe to render. The panel gets the action instead.
    if (status.error !== null) {
      console.log({ event: "control.bridge_error", message: status.error });
    }
    // Every drop goes to the console with its code, for whoever is asked to
    // read it back; the panel line is the version that needs no DevTools.
    if (status.drop !== null) console.log({ event: "control.socket_drop", ...status.drop });
    // Its OWN line, written here and nowhere else — see `problemLine`.
    text("problem", problemLine(status, relayUrl) ?? "");
    // Shown only once the bridge has stopped dialling: while it is still
    // retrying, a button that also dials would race the backoff it already has.
    document.getElementById("retry")?.toggleAttribute("hidden", !status.stopped);
  },
});

/**
 * Claim this project on the edge INTO "visible and connected" — see
 * `controlFront.ts` for why both edges and nothing else.
 */
document.addEventListener("visibilitychange", claimIfFront);

/**
 * Follow an in-tab project switch: re-dial with a token for the new project,
 * so this panel never answers for one project while reporting another. The
 * re-dial is a reconnect, so `claimIfFront` re-claims the new project if this
 * tab is in front.
 */
followProject(host, place, (next) => {
  place = next;
  text("project", next.projectId);
  text("workspace", next.workspaceId);
  bridge.relocate(next).catch((cause: unknown) => {
    console.log({
      event: "control.relocate_failed",
      message: cause instanceof Error ? cause.message : String(cause),
    });
  });
});

document.getElementById("retry")?.addEventListener("click", () => {
  bridge.retry();
});

document.getElementById("connect")?.addEventListener("click", () => {
  // The listener is sync, so the rejection is handled HERE — a dropped one
  // would leave the panel saying "Linking…" forever with nothing saying why.
  connect().catch((cause: unknown) => {
    // Same split as the status handler above: an arbitrary thrown value is
    // console detail, and the panel carries the step the user can repeat.
    console.log({
      event: "control.link_failed",
      message: cause instanceof Error ? cause.message : String(cause),
    });
    setStatus("Linking did not complete. Press Connect again.");
  });
});

/**
 * Link this browser to this Giraffe user, so `/authorize` knows whose account
 * to grant.
 *
 * One credentialed `fetch`, and it works only because this panel, the relay
 * and the Giraffe page around them are all `*.giraffe.build` — one site, so
 * the cookie the relay sets is first-party rather than partitioned. Served from
 * any other site this request is refused by the relay's `LINK_ORIGINS`, and
 * would set nothing usable if it were not. See `link()` in `mcpWorker.ts`.
 */
async function connect(): Promise<void> {
  if (relayOrigin.length === 0) {
    setStatus("This build has no relay origin on its script URL, so there is nothing to link to.");
    return;
  }
  setStatus("Linking…");
  // The token crosses HERE and only here: in a POST body to the relay's own
  // origin. Never a URL.
  const response = await fetch(`${relayOrigin}/link`, {
    method: "POST",
    body: await host.userToken(),
    credentials: "include",
  });
  if (response.ok) {
    setStatus("This browser is linked. Add the connector in claude.ai.");
    return;
  }
  // A refusal from `link()` carries `{ error }` written for a person. An origin
  // refusal carries no CORS headers, so it never gets here — `fetch` rejects.
  const body: unknown = await response.json().catch(() => null);
  const error = isJsonObject(body) ? body.error : null;
  setStatus(typeof error === "string" ? error : "Linking did not complete. Press Connect again.");
}

/**
 * The name beside a row's id, or the unlisted note (#37). The names are the
 * panel's first unbounded user-controlled strings: `textContent` makes them
 * inert, `controlPanel.css` truncates them, and `title` keeps the full text.
 */
function showName(id: string, reportedId: string, listings: readonly TokenListing[] | null): void {
  const row = nameFor(reportedId, listings);
  const value = row.kind === "named" ? row.name : row.kind === "unlisted" ? UNLISTED_NOTE : "";
  text(id, value);
  attr(id, "title", value);
  attr(id, "data-unlisted", row.kind === "unlisted" ? "true" : "false");
}

function setStatus(value: string): void {
  text("status", value);
}

function text(id: string, value: string): void {
  const node = document.getElementById(id);
  if (node !== null) node.textContent = value;
}

function attr(id: string, name: string, value: string): void {
  const node = document.getElementById(id);
  if (node !== null) node.setAttribute(name, value);
}
